import {
  NotificationService,
  OuiBoxComponent,
  OuiButtonComponent,
  OuiCenterComponent,
  OuiRecordDatasource as OuiDatasource,
  OuiFileDropZoneComponent,
  OuiGridComponent,
  OuiRowComponent,
  OuiSelectComponent,
  OuiStackComponent,
  OuiTextComponent,
} from '@aiz-gl/omni-ui';
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CardComponent } from '../../components/card/card.component';
import { DownloadButtonComponent } from '../../components/download-button/download-button.component';
import { LogTableComponent } from '../../components/log-table/log-table.component';
import { PageComponent } from '../../components/page/page';
import { SelectComponent } from '../../components/select/select.component';
import { WorkflowLogEntry } from '../../models/workflow-history.model';
import { DeviceCareCenterService } from '../../services/device-care-center/device-care-center.service';
import { PricingService } from '../../services/pricing/pricing.service';
import { SalesChannelService } from '../../services/sales-channel/sales-channel.service';
import { WarehouseService } from '../../services/warehouse/warehouse.service';
import { BasePageComponent } from '../base/base.component';

interface PricingDataDownloadForm {
  deviceCareCenter: string;
  warehouseCode: string;
}

@Component({
  selector: 'app-pricing',
  standalone: true,
  imports: [
    OuiStackComponent,
    OuiCenterComponent,
    OuiRowComponent,
    OuiBoxComponent,
    OuiGridComponent,
    OuiTextComponent,
    OuiButtonComponent,
    OuiFileDropZoneComponent,
    SelectComponent,
    DownloadButtonComponent,
    LogTableComponent,
    PageComponent,
    CardComponent,
    OuiSelectComponent,
  ],
  templateUrl: './pricing.component.html',
})
export class PricingPage extends BasePageComponent {
  public dcc = inject(DeviceCareCenterService);
  public warehouse = inject(WarehouseService);
  public pricing = inject(PricingService);

  private readonly notification = inject(NotificationService);
  protected readonly salesChannel = inject(SalesChannelService);

  permissionToUpload = this.permissionTo('pricing:manage');

  selectedFile = signal<File | undefined>(undefined);
  selectedSalesChannel = signal<string | undefined>(undefined);

  fileDropZone = viewChild<OuiFileDropZoneComponent>('fileDropZone');

  downloadForm = new FormGroup({
    deviceCareCenter: new FormControl('', Validators.required),
    warehouseCode: new FormControl('', Validators.required),
  });

  protected salesChannelCode: string | undefined;

  selectedWms = toSignal(this.downloadForm.controls.warehouseCode.valueChanges);

  selectedDcc = toSignal(
    this.downloadForm.controls.deviceCareCenter.valueChanges,
  );

  warehouseOptions = computed(() =>
    this.warehouse
      .warehouseOptions()
      .filter((option) => option.dccCode === this.selectedDcc()),
  );

  uploadHistoryDatasource?: OuiDatasource<WorkflowLogEntry>;

  constructor() {
    super();

    this.pricing.uploadHistory.reload();

    effect(() => {
      const data = this.pricing.uploadHistory.value();
      if (!data) return;
      this.uploadHistoryDatasource = new OuiDatasource<WorkflowLogEntry>(data);
    });
  }

  uploadFile() {
    const file = this.selectedFile();
    const salesChannelCode = this.salesChannelCode;

    if (!file || !salesChannelCode) return;

    this.pricing.uploadPricingFile(file, salesChannelCode).subscribe({
      next: () => {
        this.fileDropZone()?.selectedFile.set(undefined);
        this.selectedFile.set(undefined);
      },
      error: (err) => {
        const errorMessage =
          typeof err === 'string'
            ? err
            : err.error || 'Failed to upload the file. Please try again.';
        this.notification.showAlert({
          message: errorMessage,
          variant: 'warn',
          symbolName: 'error',
        });
      },
    });
  }

  handleFileAttached(file: File | undefined) {
    if (file) this.selectedFile.set(file);
  }

  pricingFileName = computed(() => {
    return [
      'ATB',
      this.selectedDcc(),
      this.selectedWms(),
      'Pricing',
      new Date().toISOString().slice(0, 10),
    ]
      .join('_')
      .replace('__', '_');
  });
}
